/**
 * Design tokens — single source of truth for shell chrome (sidebar, header, footer).
 * Extend with light/dark semantic colors as the revamp progresses.
 */

/**
 * Institute portal brand primary — use everywhere instead of legacy green (#066235, #00703C).
 * Import from `@config/theme/tokens` or Tailwind `text-primary` / `bg-primary`.
 */
export const brandPrimary = "#026CB5";
export const brandPrimaryHover = "#025c9a";
export const brandPrimaryMuted = "rgba(2, 108, 181, 0.08)";
export const brandPrimaryMutedBg = "rgba(2, 108, 181, 0.06)";

/** Light blue surfaces for Export, secondary actions (replaces legacy green-50). */
export const brandPrimarySurface = "#E8F4FC";
export const brandPrimarySurfaceHover = "#D6EBFA";
export const brandPrimarySurfaceBorder = "#C5DFF0";

/** Figma Text-color — headings and primary body text on auth cards. */
export const textColor = "#161616";

/** Standard button corner radius (Figma). */
export const buttonBorderRadius = 5;
export const buttonRadiusClassName = "rounded-button";

/** Captcha / recaptcha display box (Figma). */
export const captchaDisplayBorder = "#D9D9D9";
export const captchaDisplayBorderWidth = 1.2;
export const captchaDisplayRadius = 8;
export const captchaDisplayBackground =
	"linear-gradient(135deg, #EEF2F7 0%, #E8EDF5 100%)";

/** @deprecated Legacy green — use `brandPrimary`. */
export const legacyBrandGreen = brandPrimary;

export const shellTokens = {
	header: {
		background: '#ffffff',
		foreground: '#0f172a',
		icon: '#026CB5',
		border: '#e2e8f0',
	},
	sidebar: {
		/** Logo / brand band above navigation */
		headerBackground: '#004879',
		headerBorder: '#98D5FF',
		headerBorderWidth: '0.5px',
		/** Main navigation panel */
		primary: '#026CB5',
		foreground: '#ffffff',
		/** Selected row — same dark blue as logo header band */
		navActiveBg: '#004879',
		navActiveForeground: '#ffffff',
		navActiveIcon: '#ffffff',
		/** Left accent on selected row (Figma light blue) */
		navActiveIndicator: '#98D5FF',
		navHoverBg: 'rgba(255, 255, 255, 0.08)',
		border: 'rgba(152, 213, 255, 0.35)',
		scrollbarThumb: 'rgba(255, 255, 255, 0.35)',
		scrollbarTrack: 'rgba(0, 0, 0, 0.12)',
		toggleBackground: '#026CB5',
		toggleHover: '#004879',
		toggleForeground: '#ffffff',
	},
} as const;

/** Data tables — Figma “Recent Applications” / institute portal lists */
export const tableTokens = {
	headerBackground: "#535353",
	headerColor: "#ffffff",
	headerFontWeight: 600,
	headerFontSize: "0.8125rem",
	headerLetterSpacing: "0.64px",
	headerPadding: "14.67px 18.3px",
	headerDivider: "rgba(255, 255, 255, 0.1)",
	bodyPadding: "16px 18.3px",
	bodyColor: "#161616",
	borderColor: "#E8E8E8",
	wrapperRadius: 10,
	rowHoverBackground: "#F7FAFD",
	wrapperBorder: "1px solid #E8E8E8",
} as const;

export type SidebarShellToken = keyof typeof shellTokens.sidebar;
export type HeaderShellToken = keyof typeof shellTokens.header;
